<template>
  <section class="tt-button-options">
    <OnClickOutside @trigger="ui.navOpen = false" class="w-full h-full">
      <div
        class="inline-flex items-stretch justify-between w-full guest-ui:w-full h-full"
        ref="buttonOption"
      >
        <template v-if="buttonRules">
          <ButtonDefault
            id="button-option-drop"
            classes="rounded-none rounded-l grow self-stretch"
            :class="{
              'w-56 h-10 disabled:bg-button-secondary disabled:cursor-not-allowed': !guest,
              'grow text-button-label px-0 !font-button bg-button-guest-primary': guest,
            }"
            :disabled="disabled"
            :label="buttonRule ? `${buttonRule.then}` : `${selected.label}`"
            @click="onSubmit"
          />
        </template>
        <template v-else>
          <ButtonDefault
            id="button-option-drop-no-rule"
            :label="selected.label"
            @click="onSubmit"
            classes="rounded-none rounded-l grow self-stretch"
            :class="{
              'w-56 h-10': !guest,
              'grow text-button-label font-button px-0 bg-button-guest-primary': guest,
            }"
          />
        </template>
        <div
          class="flex justify-items-center items-center w-8 hover:cursor-pointer venue-ui:hover:bg-blue-30 drop-options-arrow guest-ui:shrink rounded-r rounded-none"
          :aria-disabled="disabled"
          :class="[
            ui.navOpen && !guest
              ? 'bg-button-primary-hover'
              : 'bg-button-primary-hover guest-ui:bg-button-guest-primary',
            {
              'bg-button-guest-primary aria-disabled:bg-button-secondary guest-ui:aria-disabled:bg-button-secondary':
                guest,
            },
          ]"
          @click="ui.navOpen = disabled ? false : !ui.navOpen"
        >
          <svg
            class="w-full guest-ui:stroke-button-label"
            width="10"
            height="4"
            viewBox="0 0 10 4"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path d="M1 1L4.96899 3L9 1" :stroke="!guest ? '#ABB9BF' : ''" />
          </svg>
        </div>
      </div>

      <div
        :class="{ block: ui.navOpen, hidden: !ui.navOpen }"
        class="flex justify-end"
        :style="{ width: `${ui.menuWidth}px` }"
      >
        <div class="mt-2 absolute z-10">
          <SlotPanel class="guest-ui:bg-surface-primary guest-ui:text-button-label">
            <div v-for="(option, key) in optionsClean" :key="key" class="">
              <span
                :id="`button-option-item-${option.value}`"
                @click="optionChanged(option)"
                :class="option.classes"
                class="font-button text-fg-link-primary guest-ui:text-primary text-base capitalize hover:cursor-pointer hover:text-header-link-hover guest-ui:text-center"
              >
                <slot name="option" v-bind="option">
                  {{ option.label }}
                </slot>
              </span>
            </div>
          </SlotPanel>
        </div>
      </div>
    </OnClickOutside>
  </section>
</template>
<script lang="ts">
import { OnClickOutside } from '@vueuse/components'
import { useElementSize } from '@vueuse/core'
import { PropType, defineComponent } from 'vue'

import ButtonDefault from '@/common/components/atoms/ButtonDefault.vue'
import SlotPanel from '@/common/components/organisms/SlotPanel.vue'

interface ButtonRule {
  when: string
  then: string
  end: string
}

export default defineComponent({
  name: 'ButtonOptions',
  components: {
    ButtonDefault,
    OnClickOutside,
    SlotPanel,
  },
  props: {
    modelValue: { type: String, required: true },
    options: { type: Array as PropType<any>, required: true },
    itemText: { type: String, required: false },
    itemValue: { type: String, required: false },
    buttonRules: { type: Object as PropType<Record<string, ButtonRule>>, required: false },
    height: { type: String, required: false, default: '2.5rem' },
    defaultOpensNav: { type: Boolean, required: false, default: false },
    disabled: { type: Boolean, required: false },
    guest: { type: Boolean, required: false },
  },
  data() {
    return {
      ui: {
        navOpen: false,
        menuWidth: null as number | null,
      },
    }
  },
  emits: {
    'update:modelValue'(evt: string) {
      return !!evt
    },
  },
  mounted() {
    // @ts-ignore
    useElementSize(this.$refs.buttonOption, { width: this.ui.menuWidth })
  },

  computed: {
    optionsClean(): any[] {
      return this.options.map(opt => ({
        ...opt,
        value: this.itemValue ? opt[this.itemValue].toString() : opt,
        label: this.itemText ? opt[this.itemText] : opt,
      }))
    },
    selected(): any {
      return this.optionsClean.find(opt => opt.value == this.modelValue)
    },
    buttonRule() {
      if (!this.buttonRules) return null
      return this.buttonRules[this.modelValue]
    },
  },

  methods: {
    optionChanged(option) {
      if (option.disabled) return
      this.$emit('update:modelValue', option.value)
      this.ui.navOpen = false
    },

    onSubmit() {
      if (this.defaultOpensNav) {
        this.ui.navOpen = true
      } else {
        if (this.buttonRule && this.buttonRule.when === this.modelValue) {
          this.$emit('update:modelValue', this.buttonRule.end)
        } else {
          this.$emit('update:modelValue', this.modelValue)
        }
      }
    },
  },
})
</script>
